import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { of } from 'rxjs';
import { filter, pluck, switchMap, tap, withLatestFrom, take, map, first } from 'rxjs/operators';

import { AssociationByCandidateIdJobIdObj } from '@app/collections/associations';
import {
  ChangeCandidateStatusModalComponent,
  ChangeCandidateStatusModalData,
} from '@modules/assigns/components/change-candidate-status-modal/change-candidate-status-modal.component';
import {
  getMoveForwardPhases,
  getRejectionAvailableStepsConfig,
  SelectedPhaseObj,
} from '@modules/assigns/store';
import { Note } from 'mojo-irp-notes';
import { notesCreate } from '@modules/notes/store/notes.actions';
import { MojoBatchService } from 'mojo-batch';
import { ModalNoteDeclinedComponent } from '@shared/modal/note-declined/modal-note-declined.component';
import {
  AssociationStatusConfiguration,
  AssociationStatusConfigurationTypes,
} from '@shared/models';
import { isTruthy } from '@shared/utils';

import {
  ModalActionTypes,
  noteBatchMoveForwardModalShow,
  noteBatchRejectedModalShow,
  noteRejectedModalShow,
  showModalApiKey,
} from './modal.actions';
import { ModalComponent } from './modal.component';
import { getNotes } from './modal.reducer';
import { ModalNoteComponent } from './note/modal-note.component';
import {
  ApiKeyFormComponent,
  ApiKeyFormData,
} from '@modules/api-management/components/api-key-form/api-key-form.component';

const MOJO_MODAL_BACKDROP_CLASS = 'mojo-modal-backdrop';
const MOJO_MODAL_PANEL_CLASS = 'mojo-modal-dialog';

@Injectable()
export class ModalEffects {
  show$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ModalActionTypes.Show),
        tap(({ confirmAction }) => {
          const modalRef = this.dialog.open(ModalComponent, {
            autoFocus: false,
            backdropClass: MOJO_MODAL_BACKDROP_CLASS,
            disableClose: true,
            panelClass: MOJO_MODAL_PANEL_CLASS,
          });
          if (confirmAction) {
            modalRef.componentInstance.confirmAction = [confirmAction];
          }
        }),
      ),
    { dispatch: false },
  );

  showNoteModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ModalActionTypes.ShowNoteModal),
        tap(({ confirmAction }) => {
          const modalRef = this.dialog.open(ModalNoteComponent, {
            autoFocus: false,
            backdropClass: MOJO_MODAL_BACKDROP_CLASS,
            disableClose: true,
            panelClass: MOJO_MODAL_PANEL_CLASS,
          });
          if (confirmAction) {
            modalRef.componentInstance.confirmAction = confirmAction;
          }
        }),
      ),
    { dispatch: false },
  );

  showCandiateRejectModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(noteRejectedModalShow),
        pluck('payload'),
        switchMap(({ jobId, candidateId, subStatus }) =>
          this.store
            .select(
              AssociationByCandidateIdJobIdObj.getAssociationByCandidateIdAndJobId(
                candidateId,
                jobId,
              ),
            )
            .pipe(
              filter(isTruthy),
              first(),
              switchMap((association) =>
                of(association).pipe(
                  withLatestFrom(
                    this.store.select(getRejectionAvailableStepsConfig, {
                      jobId,
                      phaseId: association.status,
                      stateId: subStatus,
                    }),
                  ),
                ),
              ),
            ),
        ),
        tap(([{ reqId: jobId, candidateId }, { availableSteps, initialPhase, initialState }]) => {
          let title;
          const stepType = availableSteps[0].children.find(
            (rejectionStep) => rejectionStep.id === initialState,
          ).type;
          switch (stepType) {
            case AssociationStatusConfigurationTypes.DECLINED:
              title = 'declining';
              break;
            case AssociationStatusConfigurationTypes.WITHDRAWN:
              title = 'withdrawing';
              break;
            default:
              title = 'rejection';
          }
          this.dialog.open<ChangeCandidateStatusModalComponent, ChangeCandidateStatusModalData>(
            ChangeCandidateStatusModalComponent,
            {
              panelClass: 'medium-size-modal',
              disableClose: true,
              data: {
                headerText: 'Select reason for ' + title,
                jobId,
                candidateId,
                availableSteps,
                initialPhase,
                initialState,
                reject: true,
                shouldButtonBeEnabledForOnInit: true,
              },
            },
          );
        }),
      ),
    { dispatch: false },
  );

  showBatchCandiatesRejectModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(noteBatchRejectedModalShow),
        pluck('payload'),
        switchMap(({ jobId, candidateIds, phaseId, rejectionStateId }) =>
          of({ jobId, candidateIds }).pipe(
            withLatestFrom(
              this.store.select(getRejectionAvailableStepsConfig, {
                jobId,
                phaseId,
                stateId: rejectionStateId,
              }),
            ),
          ),
        ),
        tap(([{ jobId, candidateIds }, { availableSteps, initialPhase, initialState }]) => {
          this.openBatchModal({
            headerText: 'Select reason for rejecting selected candidates',
            jobId,
            candidateIds,
            availableSteps,
            initialPhase,
            initialState,
            reject: true,
            totalMatchesAssign: false,
          });
        }),
      ),
    { dispatch: false },
  );

  showBatchCandiatesMoveForwardModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(noteBatchMoveForwardModalShow),
        switchMap(({ payload: { jobId, candidateIds, totalMatchesAssign } }) =>
          this.store.select(getMoveForwardPhases, { jobId }).pipe(
            take(1),
            tap(([phase, availableSteps]: [SelectedPhaseObj, AssociationStatusConfiguration[]]) => {
              this.openBatchModal({
                headerText: 'Moving selected candidates to shortlist',
                jobId,
                candidateIds,
                initialPhase: phase.status,
                initialState: phase.subStatus,
                availableSteps,
                reject: false,
                totalMatchesAssign,
              });
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  showNoteDeclinedModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ModalActionTypes.ShowNoteDeclinedModal),
        tap(() =>
          this.dialog.open(ModalNoteDeclinedComponent, {
            autoFocus: false,
            backdropClass: MOJO_MODAL_BACKDROP_CLASS,
            disableClose: true,
            panelClass: MOJO_MODAL_PANEL_CLASS,
          }),
        ),
      ),
    { dispatch: false },
  );

  confirmNoteModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModalActionTypes.Confirm),
      pluck('payload'),
      withLatestFrom(this.store.select(getNotes)),
      filter(([, notes]: [{ content: string }, Partial<Note>[]]) => !!notes),
      switchMap(([{ content }, notes]: [{ content: string }, Note[]]) =>
        notes.map((note: Note) => notesCreate({ payload: { ...note, content } })),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<{}>,
    private dialog: MatDialog,
    private batchSelectionManagerService: MojoBatchService,
  ) {}

  private openBatchModal({
    headerText,
    jobId,
    candidateIds,
    initialPhase,
    initialState,
    availableSteps,
    reject,
    totalMatchesAssign,
  }) {
    const dialogRef = this.dialog.open<
      ChangeCandidateStatusModalComponent,
      ChangeCandidateStatusModalData
    >(ChangeCandidateStatusModalComponent, {
      panelClass: 'medium-size-modal',
      disableClose: true,
      data: {
        headerText,
        jobId,
        batchOperation: true,
        candidateIds,
        initialPhase,
        initialState,
        availableSteps,
        reject,
        totalMatchesAssign,
        shouldButtonBeEnabledForOnInit: true,
      },
    });
    dialogRef.afterClosed().subscribe((actionTriggered) => {
      if (actionTriggered) {
        this.batchSelectionManagerService.removeAvailableItemsOnPage(candidateIds);
        this.batchSelectionManagerService.clearSelection();
      }
    });
  }

  showApiKeyModel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(showModalApiKey),
        tap(({ payload }) => {
          this.dialog.open<ApiKeyFormComponent, ApiKeyFormData>(ApiKeyFormComponent, {
            panelClass: 'large-size-modal-v2',
            disableClose: true,
            data: {
              apiKey: payload.apiKey,
            },
          });
        }),
      ),
    { dispatch: false },
  );
}
